import React, { createContext, forwardRef, useContext, useMemo } from "react";
import type { Path } from "react-hook-form";

import type { InputProps } from "@braze/beacon-core";
import { InputField } from "@braze/beacon-forms";

import { BrazeInput } from "@components/BrazeInput";

import type { BrazeInputFieldProps, OperatorInputContextValue } from "./types";

const OperatorInputContext = createContext<OperatorInputContextValue | undefined>(undefined);

const OperatorInput = forwardRef<HTMLInputElement, InputProps>((componentProps, ref) => {
  const operatorProps = useContext(OperatorInputContext);

  if (!operatorProps) {
    throw new Error("OperatorInput must be rendered within an OperatorInputContext provider");
  }

  return <BrazeInput {...componentProps} {...operatorProps} ref={ref} value={componentProps.value ?? ""} />;
});
OperatorInput.displayName = "BrazeInputFieldInput";

export const BrazeInputField = <
  TFieldValues extends Record<string, any> = Record<string, any>,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  operatorLabel,
  operatorDisabledReason,
  sensitive,
  ...rest
}: BrazeInputFieldProps<TFieldValues, TName>): React.ReactElement => {
  const operatorProps = useMemo(() => {
    return { operatorLabel, operatorDisabledReason, sensitive };
  }, [operatorLabel, operatorDisabledReason, sensitive]);

  return (
    <OperatorInputContext.Provider value={operatorProps}>
      <InputField {...rest} Component={OperatorInput} />
    </OperatorInputContext.Provider>
  );
};
